import { DisplayPairingCode, DisplayPairingPollResult, DisplayView } from '@cloudsign/shared';
import type { z } from 'zod';

/**
 * The display app has no login. After pairing it holds one device token (shown once by the server) in localStorage, so a
 * reload stays paired, and sends it as `Authorization: Device <token>` (ADR 0022).
 */

export const STORAGE_KEY = 'cloudsign.display.pairing';
const API = '/api/v1';

export interface DisplaySession {
  token: string;
  deviceId: string;
  siteId: string;
}

export function loadSession(): DisplaySession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<DisplaySession>;
    if (typeof v.token === 'string' && typeof v.deviceId === 'string' && typeof v.siteId === 'string') return { token: v.token, deviceId: v.deviceId, siteId: v.siteId };
  } catch {
    // Unreadable or blocked storage: start again from the pairing screen.
  }
  return null;
}

export function saveSession(s: DisplaySession): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // Storage blocked: the screen stays paired until the page reloads.
  }
}

export function clearSession(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored to clear.
  }
}

/** The server no longer accepts this token (the display was unpaired in the portal). */
export class DisplayRevoked extends Error {
  constructor() {
    super('display revoked');
    this.name = 'DisplayRevoked';
  }
}

async function send<S extends z.ZodType>(method: 'GET' | 'POST', path: string, schema: S, opts: { token?: string; body?: unknown } = {}): Promise<z.output<S>> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.token) headers['Authorization'] = `Device ${opts.token}`;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${API}${path}`, { method, headers, body: opts.body === undefined ? undefined : JSON.stringify(opts.body) });
  if (res.status === 401 && opts.token) throw new DisplayRevoked();
  if (!res.ok) throw new Error(`status ${res.status}`);
  return schema.parse(await res.json());
}

export const startPairing = (): Promise<z.output<typeof DisplayPairingCode>> =>
  send('POST', '/devices/display-pairing', DisplayPairingCode, { body: { userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent.slice(0, 300) } });

export const pollPairing = (pollSecret: string): Promise<DisplayPairingPollResult> => send('POST', '/devices/display-pairing/poll', DisplayPairingPollResult, { body: { pollSecret } });

export const fetchView = (token: string): Promise<DisplayView> => send('GET', '/reception-display/device/view', DisplayView, { token });
